/**
 * 霍兰德职业建议动态生成引擎
 * 
 * 根据 Holland Code 动态生成 6 条职业发展建议
 */

import {
  MAJOR_BY_TYPE,
  COMBO_FEATURES,
  SCHOOL_TYPE_BY_PRIMARY,
  COLLEGE_ACTIVITIES,
  SALARY_DATA,
  COMBO_SALARY_MODIFIER,
  GRADUATE_RULES,
  CITY_RECOMMENDATIONS,
  COMBO_CITY_SPECIAL,
  LIFE_ADVICE_TEMPLATES,
  CUSTOM_RECOMMENDATIONS
} from '@/data/hollandRecommendationRules';

/**
 * 主函数：生成完整的职业发展建议
 * @param {string} hollandCode - Holland Code (如 "ECR")
 * @returns {Array<string>} 6条建议
 */
export function generateRecommendations(hollandCode) {
  // 检查是否有完全定制的文案
  if (CUSTOM_RECOMMENDATIONS[hollandCode]) {
    return CUSTOM_RECOMMENDATIONS[hollandCode];
  }
  
  const [primary, secondary, tertiary] = hollandCode.split('');
  
  return [
    generateMajorAdvice(primary, secondary, tertiary),
    generateCollegePlanning(primary, secondary, tertiary),
    generateSalaryExpectation(primary, secondary, tertiary),
    generateGraduateAdvice(primary, secondary, tertiary),
    generateCityAdvice(primary, secondary, tertiary),
    generateLifeAdvice(primary, secondary, tertiary)
  ];
}

/**
 * 1. 生成专业选择建议
 */
function generateMajorAdvice(primary, secondary, tertiary) {
  const primaryData = MAJOR_BY_TYPE[primary];
  const secondaryData = MAJOR_BY_TYPE[secondary];
  const comboKey = primary + secondary;
  const combo = COMBO_FEATURES[comboKey] || COMBO_FEATURES[secondary + primary];
  
  // 选择专业
  const majors = [];
  majors.push(...primaryData.core.slice(0, 2));
  majors.push(...(combo?.majors || secondaryData.core.slice(0, 1)));
  
  // 组合特征描述
  const feature = combo?.desc || `${primaryData.keywords[0]}和${secondaryData.keywords[0]}相结合`;
  
  // 院校类型
  const schoolType = SCHOOL_TYPE_BY_PRIMARY[primary];
  
  // 特别说明（基于第三类型）
  const tertiaryNote = getTertiaryNote(tertiary, primary);
  
  return `📚 专业选择：推荐${majors.join('、')}等。${feature}的专业最适合你。建议选${schoolType}。${tertiaryNote}`;
}

/**
 * 2. 生成大学规划建议
 */
// eslint-disable-next-line no-unused-vars
function generateCollegePlanning(primary, secondary, _tertiary) {
  const primaryActivities = COLLEGE_ACTIVITIES[primary];
  const secondaryActivities = COLLEGE_ACTIVITIES[secondary];
  
  // 大一大二
  const year12Activities = [
    ...primaryActivities.year12.slice(0, 2),
    secondaryActivities.year12[0]
  ].join('，');
  
  // 大三
  const year3Activities = [
    primaryActivities.year3[0],
    secondaryActivities.year3[0]
  ].join('，或者');
  
  // 大四
  const year4Positions = [
    ...primaryActivities.year4.slice(0, 2),
    secondaryActivities.year4[0]
  ].join('、');
  
  return `🎯 大学规划：大一大二：${year12Activities}。大三：${year3Activities}。大四：投${year4Positions}，这些岗位${getJobFeature(primary, secondary)}。`;
}

/**
 * 3. 生成薪资预期建议
 */
// eslint-disable-next-line no-unused-vars
function generateSalaryExpectation(primary, secondary, _tertiary) {
  const primarySalary = SALARY_DATA[primary];
  const secondarySalary = SALARY_DATA[secondary];
  const comboKey = primary + secondary;
  const modifier = COMBO_SALARY_MODIFIER[comboKey] || COMBO_SALARY_MODIFIER[secondary + primary];
  
  // 应届薪资
  const freshMin = primarySalary.fresh.min;
  const freshMax = primarySalary.fresh.max;
  
  // 3-5年薪资
  const midMin = Math.round(primarySalary.mid.min * (modifier?.multiplier || 1));
  const midMax = Math.round(primarySalary.mid.max * (modifier?.multiplier || 1));
  
  // 职业类型
  const careerTypes = getCareerTypes(primary, secondary);
  
  // 薪资增长说明
  const growthNote = getSalaryGrowthNote(primary, secondary);
  
  return `💰 薪资预期：${careerTypes[0]}应届：${freshMin}-${freshMax}万（${primarySalary.fresh.note}）。${careerTypes[1] || '相关岗位'}：${secondarySalary.fresh.min}-${secondarySalary.fresh.max}万。3-5年后：${midMin}-${midMax}万。${growthNote}`;
}

/**
 * 4. 生成考研建议
 */
// eslint-disable-next-line no-unused-vars
function generateGraduateAdvice(primary, secondary, _tertiary) {
  const comboKey = primary + secondary;
  
  // 检查是否强烈推荐考研
  const shouldRecommend = GRADUATE_RULES.recommend.conditions.find(
    c => c.primary === primary || c.combo === comboKey || c.combo === secondary + primary
  );
  
  if (shouldRecommend) {
    return `🎓 考研or工作：建议考研！${shouldRecommend.reason}。选个好学校和好导师很重要，能带你做项目的那种。如果本科已经是985，可以先工作几年再读在职研究生。`;
  }
  
  // 检查是否不推荐考研
  const shouldNotRecommend = GRADUATE_RULES.notRecommend.conditions.find(
    c => c.primary === primary || c.combo === comboKey || c.combo === secondary + primary
  );
  
  if (shouldNotRecommend) {
    return `🎓 考研or工作：不建议考研！${shouldNotRecommend.reason}。建议直接工作积累实践经验。想提升可以考在职MBA或者专业培训，比全日制研究生实用。`;
  }
  
  // 视情况而定
  const conditional = GRADUATE_RULES.conditional[primary];
  if (conditional) {
    return `🎓 考研or工作：看具体方向。${conditional}。权衡好时间成本和收益，不要盲目跟风。读研要选好学校好导师，不然还不如早点工作。`;
  }
  
  // 默认建议
  return `🎓 考研or工作：建议根据目标岗位决定。想进大厂、做研发建议考研，想快速就业可以直接工作。考研要考就考好学校，二本考研性价比不高。`;
}

/**
 * 5. 生成城市选择建议
 */
// eslint-disable-next-line no-unused-vars
function generateCityAdvice(primary, secondary, _tertiary) {
  const primaryCity = CITY_RECOMMENDATIONS[primary];
  const comboKey = primary + secondary;
  const specialAdvice = COMBO_CITY_SPECIAL[comboKey] || COMBO_CITY_SPECIAL[secondary + primary];
  
  // 一线城市
  const tier1Cities = primaryCity.tier1.slice(0, 3).join('、');
  
  // 二线城市
  const tier2Cities = primaryCity.tier2.slice(0, 2).join('、');
  
  // 特殊建议
  const special = specialAdvice ? `${specialAdvice}。` : '';
  
  return `🏙️ 城市选择：推荐${tier1Cities}。${special}二线城市如${tier2Cities}也不错，生活成本低、竞争相对小。${primaryCity.note}`;
}

/**
 * 6. 生成过来人建议
 */
// eslint-disable-next-line no-unused-vars
function generateLifeAdvice(primary, secondary, _tertiary) {
  const comboKey = primary + secondary;
  const advantage = LIFE_ADVICE_TEMPLATES.advantages[comboKey] || 
                   LIFE_ADVICE_TEMPLATES.advantages[secondary + primary] ||
                   `${MAJOR_BY_TYPE[primary].keywords[0]}和${MAJOR_BY_TYPE[secondary].keywords[0]}结合是不错的组合`;
  
  // 选择注意事项
  const caution = LIFE_ADVICE_TEMPLATES.cautions[`${primary}_focus`];
  
  // 随机选择实用建议
  const practical = LIFE_ADVICE_TEMPLATES.practical[
    Math.floor(Math.random() * 3)
  ];
  
  // 选择鼓励金句
  const encouragement = LIFE_ADVICE_TEMPLATES.encouragement[
    Math.floor(Math.random() * LIFE_ADVICE_TEMPLATES.encouragement.length)
  ];
  
  return `💡 过来人建议：${advantage}。${caution || '但要保持专注，避免分散精力。'}${practical}还有，兴趣很重要，但也要考虑现实因素：就业前景、薪资水平、工作强度、家人支持。如果理想和现实有冲突，可以先就业后转型，或者找折中方案。${encouragement}`;
}

// ==================== 辅助函数 ====================

/**
 * 获取第三类型的补充说明
 */
// eslint-disable-next-line no-unused-vars
function getTertiaryNote(tertiary, _primary) {
  const notes = {
    R: '如果喜欢动手实践，可以选择实验性强的专业。',
    I: '如果喜欢深入研究，可以考虑读研继续深造。',
    A: '如果有创意天赋，可以选择设计相关的交叉学科。',
    S: '如果想帮助他人，可以选择应用型专业。',
    E: '如果有创业想法，商科背景会有帮助。',
    C: '如果注重稳定，可以选择技术+管理的复合专业。'
  };
  
  return notes[tertiary] || '根据兴趣选择最适合的方向。';
}

/**
 * 获取工作特点描述
 */
function getJobFeature(primary, secondary) {
  const features = {
    RI: '看技术能力和项目经验',
    RA: '看作品集和创意能力',
    RE: '看管理潜力和技术背景',
    IA: '看研究能力和设计水平',
    IE: '看分析能力和商业思维',
    EA: '看领导力和创新能力',
    SE: '看沟通能力和服务意识'
  };
  
  return features[primary + secondary] || features[secondary + primary] || '看综合能力';
}

/**
 * 获取职业类型
 */
function getCareerTypes(primary, secondary) {
  const types = {
    R: ['技术岗', '工程师'],
    I: ['研发岗', '数据分析师'],
    A: ['设计岗', '创意人员'],
    S: ['教育岗', '咨询师'],
    E: ['管理岗', '销售经理'],
    C: ['财务岗', '行政专员']
  };
  
  return [types[primary][0], types[secondary][0]];
}

/**
 * 获取薪资增长说明
 */
function getSalaryGrowthNote(primary, secondary) {
  if (primary === 'E' || secondary === 'E') {
    return '管理和销售类岗位增长快，能力强的话年薪几十万不是梦。';
  }
  if (primary === 'I' || secondary === 'I') {
    return '技术类岗位越老越吃香，积累经验后收入会持续增长。';
  }
  if (primary === 'A' || secondary === 'A') {
    return '创意类岗位收入波动大，做出名气后收入很可观。';
  }
  if (primary === 'C' || secondary === 'C') {
    return '财务行政类岗位收入稳定，不会大富大贵但也饿不着。';
  }
  return '坚持下来，收入会稳步增长。';
}

/**
 * 批量生成多个Holland Code的建议（用于测试）
 */
export function batchGenerate(hollandCodes) {
  const results = {};
  hollandCodes.forEach(code => {
    results[code] = generateRecommendations(code);
  });
  return results;
}

export default {
  generateRecommendations,
  batchGenerate
};
